/**
 * What a choice control offers, and where it asks for its choices when they are not written out.
 *
 * Here because stored descriptors carry both: a Form field lists its options or names the provider
 * that answers for them, and phis-server reads that field as it is stored.
 */
/**
 * `<namespace>/<name>`: an identifier that says whose it is.
 *
 * Provider keys, item renderer keys and everything else a Module may contribute to a shared registry
 * share this shape, so they share one check. A second spelling of it is the one that drifts.
 */
export function isPhiNamespacedRuntimeKey(value) {
    if (typeof value !== "string") {
        return false;
    }
    const separatorIndex = value.lastIndexOf("/");
    return separatorIndex > 0 && separatorIndex < value.length - 1;
}
export function isPhiRuntimeDataProviderKey(value) {
    return isPhiNamespacedRuntimeKey(value);
}
function readString(value) {
    return typeof value === "string" && value.trim().length > 0
        ? value.trim()
        : undefined;
}
function readRecord(value) {
    return value && typeof value === "object" && !Array.isArray(value) ? value : undefined;
}
function readLoadMode(value) {
    return value === "client" || value === "server" || value === "hybrid" ? value : undefined;
}
function readSearchConfig(value) {
    const record = readRecord(value);
    if (!record) {
        return undefined;
    }
    const enabled = typeof record.enabled === "boolean" ? record.enabled : undefined;
    const minChars = typeof record.minChars === "number" && Number.isFinite(record.minChars)
        ? Math.max(0, Math.trunc(record.minChars))
        : undefined;
    if (enabled === undefined && minChars === undefined) {
        return undefined;
    }
    return {
        enabled,
        minChars,
    };
}
function readDependencies(value) {
    if (!Array.isArray(value)) {
        return undefined;
    }
    const dependencies = value.flatMap((entry) => {
        const record = readRecord(entry);
        const param = readString(record?.param);
        const valuePath = readString(record?.valuePath);
        const source = record?.source === "form" || record?.source === "config" ? record.source : undefined;
        if (!param || !valuePath || !source) {
            return [];
        }
        return [{
                param,
                source,
                valuePath,
                required: record?.required === true ? true : undefined,
            }];
    });
    return dependencies.length > 0 ? dependencies : undefined;
}
function readOptionPreview(value) {
    const record = readRecord(value);
    if (record?.kind === "font") {
        const fontFamily = readString(record.fontFamily);
        return fontFamily ? { kind: "font", fontFamily } : undefined;
    }
    if (record?.kind !== "background")
        return undefined;
    return {
        kind: "background",
        backgroundColor: readString(record.backgroundColor),
        backgroundImage: readString(record.backgroundImage),
        backgroundSize: readString(record.backgroundSize),
        backgroundPosition: readString(record.backgroundPosition),
        backgroundRepeat: readString(record.backgroundRepeat),
    };
}
export function parsePhiControlOptionsProviderConfig(value) {
    const record = readRecord(value);
    if (!record) {
        return null;
    }
    const providerKey = readString(record.providerKey);
    if (!isPhiRuntimeDataProviderKey(providerKey)) {
        return null;
    }
    return {
        providerKey,
        scopeKey: readString(record.scopeKey),
        area: readString(record.area),
        params: readRecord(record.params),
        loadMode: readLoadMode(record.loadMode),
        search: readSearchConfig(record.search),
        dependencies: readDependencies(record.dependencies),
    };
}
export function readPhiControlOptions(value) {
    if (Array.isArray(value)) {
        return value
            .map((item) => {
            if (!item || typeof item !== "object" || Array.isArray(item)) {
                return null;
            }
            const record = item;
            const optionValue = readString(record.value);
            if (!optionValue) {
                return null;
            }
            return {
                value: optionValue,
                label: readString(record.label) ?? optionValue,
                disabled: typeof record.disabled === "boolean" ? record.disabled : undefined,
                description: readString(record.description),
                icon: readString(record.icon),
                preview: readOptionPreview(record.preview),
                group: readString(record.group),
                separator: record.separator === "before" || record.separator === "after" ? record.separator : undefined,
            };
        })
            .filter((item) => item != null);
    }
    return [];
}
